/**
 * 前端缓存策略：按「数据变化频率」分级（单位：毫秒）。
 * Web 端用于 TanStack Query 的 staleTime；小程序/App 用于 api-sdk 的 GET 内存缓存。
 */
export const CachePolicy = {
  /** 库存余额、待办、未读数等实时数据：不缓存。 */
  live: 0,
  /** 申领/配送/订单等业务列表：15 秒。 */
  transaction: 15_000,
  /** 驾驶舱/监管聚合快照（Worker 每小时刷新）：5 分钟。 */
  aggregate: 5 * 60_000,
  /** 物资、供应商、仓库、科室等主数据：10 分钟。 */
  master: 10 * 60_000,
  /** 品类、单位、行政区划等字典：30 分钟。 */
  dictionary: 30 * 60_000,
  /** TanStack Query 全局默认 staleTime：30 秒。 */
  defaultStale: 30_000
} as const;

/** 单条路径缓存规则：命中 `prefix` 本身或其子路径时使用 `ttlMs`。 */
export interface CacheRule {
  prefix: string;
  ttlMs: number;
}

/**
 * 路径前缀匹配（按路径段边界）：
 * `/wl/material` 命中 `/wl/material` 与 `/wl/material/123`，但不会命中 `/wl/material-price`。
 */
export function matchCacheRule(
  path: string,
  rules: readonly CacheRule[],
  fallbackTtlMs: number
): number {
  for (const rule of rules) {
    if (path === rule.prefix || path.startsWith(`${rule.prefix}/`)) return rule.ttlMs;
  }
  return fallbackTtlMs;
}

/**
 * 只读基础数据默认规则（小程序/App 启用）。
 * 刻意不包含库存、审批、配送状态、结算等强一致数据。
 */
export const defaultCacheRules: readonly CacheRule[] = [
  { prefix: '/auth/org-config', ttlMs: CachePolicy.dictionary },
  { prefix: '/sys/warehouse', ttlMs: CachePolicy.master },
  { prefix: '/sys/dept', ttlMs: CachePolicy.master },
  { prefix: '/sys/building', ttlMs: CachePolicy.master },
  { prefix: '/sys/floor', ttlMs: CachePolicy.master },
  { prefix: '/sys/location', ttlMs: CachePolicy.master },
  { prefix: '/wl/category', ttlMs: CachePolicy.dictionary },
  { prefix: '/wl/unit', ttlMs: CachePolicy.dictionary },
  { prefix: '/wl/material', ttlMs: CachePolicy.master },
  { prefix: '/wl/supplier', ttlMs: CachePolicy.master },
  { prefix: '/wl/catalog', ttlMs: CachePolicy.master },
  { prefix: '/gov', ttlMs: CachePolicy.aggregate },
  { prefix: '/wl/analytics', ttlMs: CachePolicy.aggregate }
];
